import { Injectable } from '@angular/core';
import { FSelectionChangeEvent, injectFlowState } from '@foblex/flow';
import { FlowNodeHelpers } from '../helpers/flow-node.helpers';
import { FlowNode, FlowState, FlowStateConnection } from '../models';

/**
 * App-level operations on the flow state. Gestures (move, select, create/reassign connection, palette drop,
 * pan/zoom) are applied by the Foblex `withFlowState()` auto-wiring; this service covers what the app adds on top.
 */
@Injectable({
  providedIn: 'root'
})
export class FlowApiService {
  private readonly state = injectFlowState<FlowNode, FlowStateConnection, FlowNode>();

  /** Record-shaped view of the state, consumed by the flow helpers and the domain services. */
  getState(): FlowState {
    const { nodeIds, groupIds, connectionIds } = this.state.selection();
    const { position, scale } = this.state.transform();
    return {
      nodes: Object.fromEntries([...this.state.nodes(), ...this.state.groups()].map((n) => [n.id, n])),
      connections: Object.fromEntries(this.state.connections().map((c) => [c.id, { ...c, sourcePortId: c.sourceId, targetPortId: c.targetId }])),
      selection: { nodes: [...nodeIds, ...groupIds], connections: [...connectionIds] },
      transform: position ? { position, scale } : undefined
    };
  }

  updateState({ nodes, connections }: Partial<FlowState>): void {
    this.state.batch(() => {
      Object.values(nodes ?? {}).forEach((node) =>
        this.state.getGroup(node.id) ? this.state.updateGroup(node.id, node) : this.state.updateNode(node.id, node)
      );
      Object.values(connections ?? {}).forEach((connection) => this.state.updateConnection(connection.id, connection));
    });
  }

  selectAll(): void {
    this.select(
      this.state.nodes().map((n) => n.id),
      this.state.groups().map((g) => g.id),
      this.state.connections().map((c) => c.id)
    );
  }

  deselectAll(): void {
    this.select([], [], []);
  }

  removeConnection(outputId: string): void {
    const id = this.state.connections().find((c) => c.sourceId === outputId)?.id;
    if (id) {
      this.state.removeConnections([id]);
    }
  }

  /** Removes the selection with the children of the selected nodes; attached connections cascade. */
  removeSelected(): void {
    const { nodeIds, groupIds, connectionIds } = this.state.selection();
    const ids = this.getNodeIdsWithChildren([...nodeIds, ...groupIds]);
    if (!ids.length && !connectionIds.length) {
      return;
    }
    const isGroup = (id: string) => !!this.state.getGroup(id);
    this.state.batch(() => {
      this.state.removeItems(
        ids.filter((id) => !isGroup(id)),
        connectionIds
      );
      this.state.removeGroups(ids.filter(isGroup));
    });
    this.deselectAll();
  }

  getNodeIdsWithChildren(nodeIds: string[]): string[] {
    const nodes = Object.values(this.getState().nodes);
    for (const nodeId of nodeIds) {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) continue;
      for (const child of FlowNodeHelpers.findChildren(node, nodes)) {
        if (!nodeIds.includes(child.id)) {
          nodeIds.push(child.id);
        }
      }
    }
    return nodeIds;
  }

  private select(nodeIds: string[], groupIds: string[], connectionIds: string[]): void {
    this.state.applySelectionChange(new FSelectionChangeEvent(nodeIds, groupIds, connectionIds));
  }
}
